import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { showSuccess, showError } from '../utils/toast';
import {
    FaPaperPlane,
    FaExclamationTriangle,
    FaUsers,
    FaClock,
    FaArrowLeft,
    FaCheck
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const AdminSendEmail = () => {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    // User selection
    const [sendToAll, setSendToAll] = useState(true);
    const [users, setUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    // Email stats
    const [emailStats, setEmailStats] = useState({ todayCount: 0, remaining: 100, totalUsers: 0 });

    useEffect(() => {
        if (authLoading) return;

        if (!user || user.role !== 'admin') {
            navigate('/');
            return;
        }

        fetchEmailStats();
    }, [user, authLoading, navigate]);

    useEffect(() => {
        if (!sendToAll && users.length === 0) {
            fetchUsers();
        }
    }, [sendToAll]);

    const fetchEmailStats = async () => {
        try {
            const { data } = await axios.get('/api/admin/email-stats');
            setEmailStats(data);
        } catch (error) {
            console.error('Email stats alınamadı:', error);
        }
    };

    const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
            const { data } = await axios.get('/api/admin/users');
            setUsers(data);
            setSelectedUsers(data.map(u => u._id)); // Başta hepsi seçili
        } catch (error) {
            console.error('Kullanıcılar alınamadı:', error);
        } finally {
            setLoadingUsers(false);
        }
    };

    const toggleUser = (userId) => {
        setSelectedUsers(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const toggleSelectAll = () => {
        if (selectedUsers.length === users.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(users.map(u => u._id));
        }
    };

    const getRecipientCount = () => {
        return sendToAll ? emailStats.totalUsers : selectedUsers.length;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!subject.trim() || !message.trim()) {
            showError('Konu ve mesaj alanları zorunludur');
            return;
        }

        const recipientCount = getRecipientCount();

        if (recipientCount === 0) {
            showError('En az bir kullanıcı seçmelisiniz');
            return;
        }

        if (!window.confirm(`${recipientCount} kullanıcıya email göndermek istediğinize emin misiniz?`)) {
            return;
        }

        setLoading(true);
        setResult(null);

        try {
            const payload = {
                subject,
                message,
                ...(sendToAll ? {} : { userIds: selectedUsers })
            };

            const { data } = await axios.post('/api/admin/send-bulk-email', payload);

            setResult(data);
            showSuccess(`${data.successCount} kullanıcıya email gönderildi!`);
            setSubject('');
            setMessage('');
            fetchEmailStats(); // Sayacı güncelle
        } catch (error) {
            showError(error.response?.data?.message || 'Email gönderimi başarısız');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8 transition-colors duration-300 ease-in-out">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8 flex items-start justify-between">
                    <div>
                        <Link
                            to="/admin/dashboard"
                            className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4"
                        >
                            <FaArrowLeft className="mr-2" />
                            Admin Panel'e Dön
                        </Link>
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                            Toplu Email Gönder
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400">
                            Kayıtlı kullanıcılara email gönder
                        </p>
                    </div>

                    {/* Günlük Sayaç (köşede) */}
                    <div className="text-right text-sm text-gray-500 dark:text-gray-400">
                        <div className="bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-lg">
                            <span className={emailStats.todayCount > 80 ? 'text-red-500 font-semibold' : ''}>
                                {emailStats.todayCount}/100
                            </span>
                            <span className="block text-xs">bugün gönderildi</span>
                        </div>
                    </div>
                </div>

                {/* Resend Limit Uyarısı - sadece limit yaklaştığında */}
                {emailStats.remaining < 30 && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 p-4 mb-6 rounded-r-lg">
                        <div className="flex items-start">
                            <FaExclamationTriangle className="text-yellow-400 text-xl mt-0.5 mr-3 flex-shrink-0" />
                            <div>
                                <h3 className="text-yellow-800 dark:text-yellow-200 font-semibold">
                                    Günlük Limit Uyarısı
                                </h3>
                                <p className="text-yellow-700 dark:text-yellow-300 text-sm mt-1">
                                    Bugün <strong>{emailStats.remaining}</strong> email daha gönderebilirsiniz.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Alıcı Seçimi */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                            <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-full mr-4">
                                <FaUsers className="text-blue-600 dark:text-blue-400 text-xl" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Alıcı Sayısı</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {getRecipientCount()} Kullanıcı
                                </p>
                            </div>
                        </div>

                        {/* Tümüne Gönder Toggle */}
                        <label className="flex items-center cursor-pointer">
                            <span className="mr-3 text-sm text-gray-700 dark:text-gray-300">Tümüne Gönder</span>
                            <div className="relative">
                                <input
                                    type="checkbox"
                                    checked={sendToAll}
                                    onChange={(e) => setSendToAll(e.target.checked)}
                                    className="sr-only"
                                />
                                <div className={`block w-14 h-8 rounded-full ${sendToAll ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                                <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition ${sendToAll ? 'transform translate-x-6' : ''}`}></div>
                            </div>
                        </label>
                    </div>

                    {/* Kullanıcı Listesi (toggle off olunca) */}
                    {!sendToAll && (
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Kullanıcıları Seç ({selectedUsers.length}/{users.length})
                                </p>
                                <button
                                    type="button"
                                    onClick={toggleSelectAll}
                                    className="text-sm text-blue-600 hover:text-blue-700"
                                >
                                    {selectedUsers.length === users.length ? 'Hiçbirini Seçme' : 'Tümünü Seç'}
                                </button>
                            </div>

                            {loadingUsers ? (
                                <div className="flex justify-center py-4">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                </div>
                            ) : (
                                <div className="max-h-60 overflow-y-auto space-y-2">
                                    {users.map(u => (
                                        <label
                                            key={u._id}
                                            className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${selectedUsers.includes(u._id)
                                                ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800'
                                                : 'bg-gray-50 dark:bg-gray-700 border border-transparent hover:bg-gray-100 dark:hover:bg-gray-600'
                                                }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedUsers.includes(u._id)}
                                                onChange={() => toggleUser(u._id)}
                                                className="sr-only"
                                            />
                                            <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${selectedUsers.includes(u._id)
                                                ? 'bg-blue-600 border-blue-600'
                                                : 'border-gray-300 dark:border-gray-500'
                                                }`}>
                                                {selectedUsers.includes(u._id) && <FaCheck className="text-white text-xs" />}
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-900 dark:text-white">{u.name}</p>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">{u.email}</p>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Email Formu */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6 border border-gray-200 dark:border-gray-700">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Konu *
                            </label>
                            <input
                                type="text"
                                id="subject"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="Email konusu..."
                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                disabled={loading}
                            />
                        </div>

                        <div>
                            <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Mesaj *
                            </label>
                            <textarea
                                id="message"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Email içeriğini yazın..."
                                rows={8}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                                disabled={loading}
                            />
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                Not: Email "Döviz Defteri Bilgilendirme" başlığı, siteye yönlendirme butonu ve standart footer ile gönderilir.
                            </p>
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !subject.trim() || !message.trim() || getRecipientCount() === 0}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                                    <span>Gönderiliyor... (Bu işlem biraz zaman alabilir)</span>
                                </>
                            ) : (
                                <>
                                    <FaPaperPlane />
                                    <span>{sendToAll ? 'Tüm Kullanıcılara Gönder' : `${selectedUsers.length} Kullanıcıya Gönder`}</span>
                                </>
                            )}
                        </button>
                    </form>
                </div>

                {/* Sonuç */}
                {result && (
                    <div className={`rounded-xl shadow-lg p-6 mb-6 border ${result.failedCount > 0
                        ? 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-400'
                        : 'bg-green-50 dark:bg-green-900/30 border-green-400'
                        }`}>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                            Gönderim Sonucu
                        </h3>
                        <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                                <p className="text-3xl font-bold text-blue-600">{result.totalUsers}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Toplam</p>
                            </div>
                            <div>
                                <p className="text-3xl font-bold text-green-600">{result.successCount}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Başarılı</p>
                            </div>
                            <div>
                                <p className="text-3xl font-bold text-red-600">{result.failedCount}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Başarısız</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Coming Soon - Haftalık Bülten */}
                <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 opacity-60">
                    <div className="flex items-start">
                        <div className="bg-gray-200 dark:bg-gray-700 p-3 rounded-full mr-4">
                            <FaClock className="text-gray-500 dark:text-gray-400 text-xl" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                                Otomatik Haftalık Bülten (Yakında)
                            </h3>
                            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                                Bu özellik ile kullanıcılara portföy özetleri otomatik gönderilecek.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminSendEmail;
